import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const ClientDocumentsPage = () => {
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [previewDoc, setPreviewDoc] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);

  useEffect(() => {
    fetchClientDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useEffect(() => {
    return () => {
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    };
  }, [pdfBlobUrl]);

  const fetchClientDocuments = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`/api/comptable/public/client/${clientId}/documents`);
      setClient(response.data.client);
      setDocuments(response.data.documents);
    } catch (err) {
      setError('Impossible de charger les documents');
      console.error('Erreur:', err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non spécifiée';
    try {
      return new Date(dateString).toLocaleDateString('fr-FR');
    } catch {
      return dateString;
    }
  };

  const getTypeLabel = (type) => {
    const types = { emis: 'Émis', recu: 'Reçu', declaration: 'Déclaration', autre: 'Autre' };
    return types[type] || type;
  };

  const getStatusColor = (status) => {
    const statusMap = { ACTIVE: '#4CAF50', ANNULE: '#f44336', RECTIFIED: '#FF9800' };
    return statusMap[status] || '#666';
  };

  const handlePreview = async (doc) => {
    try {
      setPreviewLoading(true);
      setPreviewDoc(doc);
      setShowPreview(true);

      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
        setPdfBlobUrl(null);
      }

      const res = await axios.get(
        `/api/comptable/public/document/${doc._id}/preview`,
        { responseType: 'arraybuffer', withCredentials: true }
      );

      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfBlobUrl(url);
    } catch (err) {
      console.error('Erreur preview:', err.response?.data || err.message);
      alert('Erreur lors de la prévisualisation: ' + (err.response?.data?.error || err.message));
      setShowPreview(false);
      setPreviewDoc(null);
      setPdfBlobUrl(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setShowPreview(false);
    setPreviewDoc(null);
    setPreviewLoading(false);

    if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    setPdfBlobUrl(null);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Chargement des documents...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-icon">❌</div>
        <h3>Erreur</h3>
        <p>{error}</p>
        <button onClick={fetchClientDocuments}>Réessayer</button>
      </div>
    );
  }

  return (
    <div className="client-documents-container">
      <header className="page-header">
        <h1>📄 Documents de {client?.raisonSociale || `${client?.prenom} ${client?.nom}`}</h1>
        <p className="doc-count">
          Total: {documents.length} document{documents.length > 1 ? 's' : ''}
        </p>
      </header>

      <div className="documents-list">
        {documents.length === 0 ? (
          <div className="no-documents">
            <div className="empty-icon">📭</div>
            <p>Aucun document disponible pour ce client.</p>
          </div>
        ) : (
            <table className="documents-table">
              <thead>
                <tr>
                  <th>Nom du document</th>
                  <th>Type</th>
                  <th>Date de paiement</th>
                  <th>Montant HT</th>
                  <th>Source</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => (
                  <tr key={doc._id}>
                    <td className="doc-name">
                      <strong>{doc.originalName || doc.fileName}</strong>
                    </td>
                    <td>
                      <span className={`doc-type ${doc.classification.type}`}>
                        {getTypeLabel(doc.classification.type)}
                      </span>
                    </td>
                    <td>{formatDate(doc.paymentDate)}</td>
                    <td className="amount">
                      {doc.montantHT ? `${doc.montantHT.toLocaleString('fr-FR')} TND` : '-'}
                    </td>
                    <td>{doc.source === 'generated' ? 'TEJ généré' : 'TEJ reçu'}</td>
                    <td>
                      <span
                        className="doc-status"
                        style={{ backgroundColor: getStatusColor(doc.tejState), color: 'white' }}
                      >
                        {doc.tejState}
                      </span>
                    </td>
                    <td className="actions">
                      <button
                        className="btn-preview"
                        onClick={() => handlePreview(doc)}
                        title="Prévisualiser le document"
                        disabled={previewLoading && previewDoc?._id === doc._id}
                      >
                        {previewLoading && previewDoc?._id === doc._id ? <>⏳ Chargement...</> : <>👁️ Prévisualiser</>}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      {/* Modal de prévisualisation */}
      {showPreview && previewDoc && (
        <div className="preview-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{previewDoc.originalName || previewDoc.fileName}</h3>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                {pdfBlobUrl && (
                  <a
                    href={pdfBlobUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: '#fff', textDecoration: 'underline', fontSize: 13 }}
                  >
                    Ouvrir dans un onglet
                  </a>
                )}
                <button className="close-btn" onClick={closePreview}>×</button>
              </div>
            </div>

            <div className="modal-body" style={{ overflow: 'hidden', padding: 0 }}>
              {previewLoading ? (
                <div className="preview-loading">
                  <div className="spinner"></div>
                  <p>Chargement du document...</p>
                </div>
              ) : pdfBlobUrl ? (
                <iframe
                  src={pdfBlobUrl}
                  title="Prévisualisation PDF"
                  style={{ width: '100%', height: '100%', border: 'none' }}
                />
              ) : (
                <div className="preview-error">
                  <div className="error-icon">❌</div>
                  <p>Impossible de charger le document</p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-close" onClick={closePreview}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .client-documents-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
          font-family: Arial, sans-serif;
        }
        .page-header {
          background: linear-gradient(135deg, #4caf50 0%, #388e3c 100%);
          color: white;
          padding: 30px;
          border-radius: 10px;
          margin-bottom: 30px;
          text-align: center;
        }
        .page-header h1 {
          margin: 0;
          font-size: 28px;
        }
        .doc-count {
          margin: 10px 0 0;
          font-size: 18px;
          opacity: 0.9;
        }
        .loading-container,
        .preview-loading {
          text-align: center;
          padding: 100px 20px;
        }
        .spinner {
          border: 5px solid #f3f3f3;
          border-top: 5px solid #4caf50;
          border-radius: 50%;
          width: 50px;
          height: 50px;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .error-container,
        .preview-error {
          text-align: center;
          padding: 100px 20px;
          background: #ffebee;
          border-radius: 10px;
          border: 1px solid #f44336;
        }
        .error-icon {
          font-size: 60px;
          margin-bottom: 20px;
        }
        .error-container button {
          background: #4caf50;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 5px;
          cursor: pointer;
          margin-top: 20px;
        }
        .documents-table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          border-radius: 8px;
          overflow: hidden;
        }
        .documents-table th {
          background: #2c3e50;
          color: white;
          padding: 15px;
          text-align: left;
          font-weight: bold;
        }
        .documents-table td {
          padding: 15px;
          border-bottom: 1px solid #eee;
        }
        .documents-table tr:hover {
          background: #f9f9f9;
        }
        .doc-type {
          padding: 5px 10px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: bold;
        }
        .doc-type.emis { background: #e3f2fd; color: #1976d2; }
        .doc-type.recu { background: #e8f5e8; color: #2e7d32; }
        .doc-type.declaration { background: #fff3e0; color: #e65100; }
        .doc-type.autre { background: #f5f5f5; color: #666; }
        .doc-status {
          padding: 5px 10px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: bold;
        }
        .amount { font-weight: bold; color: #2e7d32; }
        .actions { display: flex; gap: 10px; }
        .btn-preview {
          padding: 8px 15px;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.3s;
          background: #2196f3;
          color: white;
          min-width: 120px;
        }
        .btn-preview:hover { background: #1976d2; }
        .btn-preview:disabled { background: #90caf9; cursor: not-allowed; }
        .no-documents {
          text-align: center;
          padding: 100px 20px;
          background: #f5f5f5;
          border-radius: 10px;
          border: 2px dashed #ddd;
        }
        .empty-icon {
          font-size: 60px;
          margin-bottom: 20px;
          opacity: 0.5;
        }

        .preview-modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }
        .modal-content {
          background: white;
          width: 90%;
          height: 90%;
          border-radius: 10px;
          display: flex;
          flex-direction: column;
        }
        .modal-header {
          padding: 20px;
          background: #2c3e50;
          color: white;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-radius: 10px 10px 0 0;
        }
        .modal-header h3 {
          margin: 0;
          font-size: 18px;
        }
        .close-btn {
          background: none;
          border: none;
          color: white;
          font-size: 30px;
          cursor: pointer;
          line-height: 1;
        }
        .modal-body {
          flex: 1;
          background: #fafafa;
        }
        .modal-footer {
          padding: 15px;
          background: #f5f5f5;
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          border-radius: 0 0 10px 10px;
        }
        .btn-close {
          background: #757575;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 5px;
          cursor: pointer;
        }
        @media (max-width: 768px) {
          .documents-table { display: block; overflow-x: auto; }
          .actions { flex-direction: column; gap: 5px; }
          .btn-preview { width: 100%; text-align: center; }
          .modal-content { width: 95%; height: 95%; }
        }
      `}</style>
    </div>
  );
};

export default ClientDocumentsPage;
